import {
  OmpAccentStyle,
  OmpButtonGroup,
  OmpButtonPlacement,
  OmpCalloutIcon,
  OmpCalloutLayout,
  OmpCalloutTone,
  OmpColumnsLayout,
  OmpColumnsVerticalAlign,
  OmpContentAlignment,
  OmpControlKey,
  OmpHeadingSize,
  OmpImageFit,
  OmpImageRatio,
  OmpLegacyControlKey,
  OmpMobileLayout,
  OmpMoment,
  OmpMomentActionDef,
  OmpMomentActionKey,
  OmpPage,
  OmpPillButtonStyle,
  OmpSection,
  OmpSectionConfig,
  OmpSectionDef,
  OmpSectionKey,
  OmpSectionPosition,
  OmpTextImageLayout,
  OmpVideoAspect,
} from '../_models';

// Order Management Portal editor configuration.

export const OmpSidebarSections: OmpSectionConfig[] = [
  { id: OmpSection.PageHeader, label: 'Page Header', hasInfo: true, help: 'Logo and top bar of every portal page.' },
  { id: OmpSection.ThemeColors, label: 'Theme Colors', hasInfo: true, help: 'Colors used across the portal.' },
  {
    id: OmpSection.CardStyle,
    label: 'Card Style',
    hasInfo: true,
    help: 'Border and shadow of the cards sections sit in.',
  },
  { id: OmpSection.Images, label: 'Images', hasInfo: false },
  { id: OmpSection.WidthAndHeight, label: 'Width and Height', hasInfo: false },
  { id: OmpSection.Typography, label: 'Typography', hasInfo: true, help: 'Fonts used for headings and body text.' },
  {
    id: OmpSection.Layout,
    label: 'Layout',
    hasInfo: true,
    help: 'Where navigation, pop-ups and buttons sit on the page.',
  },
  {
    id: OmpSection.ButtonsAndInteractions,
    label: 'Buttons and Interactions',
    hasInfo: true,
    help: 'Appearance of the portal buttons.',
  },
];

export const OmpPages: { key: OmpPage; label: string }[] = [
  { key: OmpPage.Home, label: 'Home' },
  { key: OmpPage.Subscriptions, label: 'Subscriptions' },
  { key: OmpPage.SubscriptionDetails, label: 'Subscription Details' },
  { key: OmpPage.Orders, label: 'Orders' },
  { key: OmpPage.OrderDetails, label: 'Order Details' },
];

export const OmpPagesWithSections: OmpPage[] = [
  OmpPage.Home,
  OmpPage.Subscriptions,
  OmpPage.SubscriptionDetails,
  OmpPage.Orders,
  OmpPage.OrderDetails,
];

export const OmpMomentKey: { [moment: number]: string } = {
  [OmpMoment.None]: 'default',
  [OmpMoment.OrderPending]: 'order_pending',
  [OmpMoment.OrderShipped]: 'order_shipped',
  [OmpMoment.SubscriptionActive]: 'subscription_active',
  [OmpMoment.SubscriptionCancelled]: 'subscription_cancelled',
  [OmpMoment.PaymentIssue]: 'payment_issue',
  [OmpMoment.Trial]: 'trial',
};

export const OmpMoments: { key: OmpMoment; label: string }[] = [
  { key: OmpMoment.None, label: 'Default' },
  { key: OmpMoment.SubscriptionActive, label: 'Subscription Active' },
  { key: OmpMoment.OrderPending, label: 'Order Pending' },
  { key: OmpMoment.OrderShipped, label: 'Order Shipped' },
  { key: OmpMoment.SubscriptionCancelled, label: 'Subscription Cancelled' },
  { key: OmpMoment.PaymentIssue, label: 'Payment Issue' },
  { key: OmpMoment.Trial, label: 'Trial' },
];

export function ompMomentLabel(moment: OmpMoment): string {
  let label = '';
  const match = OmpMoments.filter((m) => m.key === moment);
  if (match.length) {
    label = match[0].label;
  }
  return label;
}

export const OmpMomentList: OmpMoment[] = OmpMoments.map((m) => m.key);
export const OmpDefaultMoment: OmpMoment = OmpMoment.None;

export const OmpMomentPages: OmpPage[] = [OmpPage.Home, OmpPage.SubscriptionDetails, OmpPage.OrderDetails];

export const OmpPageMoments: { [page: string]: OmpMoment[] } = {
  [OmpPage.Home]: OmpMomentList,
  [OmpPage.SubscriptionDetails]: [OmpMoment.None, OmpMoment.SubscriptionActive, OmpMoment.SubscriptionCancelled],
  [OmpPage.OrderDetails]: [OmpMoment.None, OmpMoment.OrderPending, OmpMoment.OrderShipped],
};

export function ompPageHasMoments(page: string): boolean {
  return OmpMomentPages.indexOf(page as OmpPage) >= 0;
}

export function ompMomentsForPage(page: string): OmpMoment[] {
  return OmpPageMoments[page] ? OmpPageMoments[page] : [OmpMoment.None];
}

export function ompPageHasMoment(page: string, moment: OmpMoment): boolean {
  return ompMomentsForPage(page).indexOf(moment) >= 0;
}

export const OmpRetiredSectionFields: { [field: string]: string } = {
  card_background_color: 'background_color',
  card_border_color: 'border_color',
};

export const OmpRetiredStyleVars: { [key: string]: string } = {
  cancel_button_placement: '',
  return_button_placement: '',
  pause_button_placement: '',
  enable_sticky_header: 'enable_sticky_navigational_blocks',
  primary_button_corner_roundness: 'button_corner_roundness',
  secondary_button_corner_roundness: '',
  tertiary_button_corner_roundness: '',
  order_price_on_details_page: '',
};

export interface OmpControlButton {
  key: OmpControlKey;
  toggle: string;
  labelField: string;
  placementField: string;
  label: string;
  backendLabel: string;
  displayLabel: string;
  placeable: boolean;
}

export function ompControlToggleField(key: OmpControlKey | OmpLegacyControlKey): string {
  return 'show_' + key;
}

export function ompControlLabelField(key: OmpControlKey | OmpLegacyControlKey): string {
  return key + '_label';
}

export function ompControlPlacementField(key: OmpControlKey): string {
  return key + '_placement';
}

function ompControl(
  key: OmpControlKey,
  label: string,
  backendLabel: string,
  placeable: boolean = false
): OmpControlButton {
  return {
    key: key,
    toggle: ompControlToggleField(key),
    labelField: ompControlLabelField(key),
    placementField: ompControlPlacementField(key),
    label: label,
    backendLabel: backendLabel,
    displayLabel: backendLabel ? backendLabel : label,
    placeable: placeable,
  };
}

export const OmpControlButtons: { [key: string]: OmpControlButton } = {
  [OmpControlKey.ChangeVariant]: ompControl(OmpControlKey.ChangeVariant, 'Change Variant', 'Change Order Variant'),
  [OmpControlKey.RevisePackage]: ompControl(OmpControlKey.RevisePackage, 'Revise Package', 'Revise Package'),
  [OmpControlKey.SwapProduct]: ompControl(OmpControlKey.SwapProduct, 'Swap Product', 'Change Product'),
  [OmpControlKey.BuyAgain]: ompControl(OmpControlKey.BuyAgain, 'Buy Again', ''),
  [OmpControlKey.TrackPackage]: ompControl(OmpControlKey.TrackPackage, 'Track Package', 'Track Shipment'),
  [OmpControlKey.ReturnProduct]: ompControl(OmpControlKey.ReturnProduct, 'Return Product', 'Return Product', true),
  [OmpControlKey.CancelShipment]: ompControl(OmpControlKey.CancelShipment, 'Cancel Shipment', 'Cancel Shipment', true),
  [OmpControlKey.CancelSubscription]: ompControl(
    OmpControlKey.CancelSubscription,
    'Cancel Subscription',
    'Cancel Subscription',
    true
  ),
  [OmpControlKey.SendNow]: ompControl(OmpControlKey.SendNow, 'Send Now', ''),
  [OmpControlKey.Skip]: ompControl(OmpControlKey.Skip, 'Skip', 'Skip Shipment'),
  [OmpControlKey.Pause]: ompControl(OmpControlKey.Pause, 'Delay', 'Pause Subscription', true),
  [OmpControlKey.Reschedule]: ompControl(OmpControlKey.Reschedule, 'Reschedule', 'Shipping Frequency'),
  [OmpControlKey.Reactivate]: ompControl(OmpControlKey.Reactivate, 'Reactivate & Save', 'Re-Activate'),
};

export const OmpOrderControlMoments: { [moment: number]: OmpControlKey[] } = {
  [OmpMoment.None]: [
    OmpControlKey.ChangeVariant,
    OmpControlKey.RevisePackage,
    OmpControlKey.BuyAgain,
    OmpControlKey.CancelShipment,
    OmpControlKey.TrackPackage,
    OmpControlKey.ReturnProduct,
  ],
  [OmpMoment.OrderPending]: [
    OmpControlKey.ChangeVariant,
    OmpControlKey.RevisePackage,
    OmpControlKey.BuyAgain,
    OmpControlKey.CancelShipment,
  ],
  [OmpMoment.OrderShipped]: [OmpControlKey.TrackPackage, OmpControlKey.BuyAgain, OmpControlKey.ReturnProduct],
};

export const OmpSubscriptionControlMoments: { [moment: number]: OmpControlKey[] } = {
  [OmpMoment.None]: [
    OmpControlKey.SendNow,
    OmpControlKey.SwapProduct,
    OmpControlKey.Skip,
    OmpControlKey.Pause,
    OmpControlKey.Reschedule,
    OmpControlKey.BuyAgain,
    OmpControlKey.TrackPackage,
    OmpControlKey.ReturnProduct,
    OmpControlKey.CancelSubscription,
    OmpControlKey.Reactivate,
  ],
  [OmpMoment.SubscriptionActive]: [
    OmpControlKey.SendNow,
    OmpControlKey.SwapProduct,
    OmpControlKey.Skip,
    OmpControlKey.Pause,
    OmpControlKey.Reschedule,
    OmpControlKey.CancelSubscription,
  ],
  [OmpMoment.SubscriptionCancelled]: [OmpControlKey.Reactivate, OmpControlKey.BuyAgain],
};

export function ompControlKeys(page: string, moment: OmpMoment): OmpControlKey[] {
  let keys: OmpControlKey[] = [];
  if (page === OmpPage.OrderDetails && OmpOrderControlMoments[moment]) {
    keys = OmpOrderControlMoments[moment];
  } else if (page === OmpPage.SubscriptionDetails && OmpSubscriptionControlMoments[moment]) {
    keys = OmpSubscriptionControlMoments[moment];
  }
  return keys;
}

export function ompControlSeedLabel(key: OmpControlKey): string {
  const button = OmpControlButtons[key];
  return button.label === button.displayLabel ? '' : button.label;
}

export const OmpControlPlacementOptions: { value: OmpButtonPlacement; label: string }[] = [
  { value: OmpButtonPlacement.OrderControls, label: 'In the controls card' },
  { value: OmpButtonPlacement.PageBottom, label: 'Page bottom' },
];

export const OmpPageBottomOrderField = 'page_bottom_order';

export const OmpButtonLabelMaxLength = 25;

// Cross-sell fits four 288px cards (+ 3 x 16px gaps = 1200px) only at the widest Maximum Content Width,
// three otherwise.
// Mirrors crossSellMaxColumns() in visualivr-path-react; keep the two in step.
export const OmpCrossSellWideContentWidth = 1200;
export const OmpCrossSellMaxColumns = 4;
export const OmpCrossSellNarrowMaxColumns = 3;

export const OmpSectionRowHintLength = 24;
export const OmpSectionRowHintFields: string[] = ['section_heading', 'eyebrow', 'alt_text', 'video_url'];

export const OmpStyleVar = {
  HeaderBackgroundColor: 'header_background_color',
  LogoWidth: 'logo_width',
  DesktopLogoPosition: 'desktop_logo_position',
  ShowHeaderSeparatorLine: 'show_header_separator_line',
  ThemeColor: 'theme_color',
  ButtonTextColor: 'button_text_color',
  ButtonHoverColor: 'button_hover_color',
  LinksAndIconsColor: 'links_and_icons_color',
  CardBackgroundColor: 'card_background_color',
  CardBorderColor: 'card_border_color',
  PageBackgroundColor: 'page_background_color',
  Logo: 'logo',
  PageBackgroundImage: 'page_background_image',
  PageBackgroundImageOpacity: 'page_background_image_opacity',
  BodyTextColor: 'body_text_color',
  ButtonCornerRoundness: 'button_corner_roundness',
  CardCornerRoundness: 'card_corner_roundness',
  CardBorderStyle: 'card_border_style',
  CardBorderThickness: 'card_border_thickness',
  CardBorderOpacity: 'card_border_opacity',
  CardShadowOpacity: 'card_shadow_opacity',
  CardShadowHorizontalOffset: 'card_shadow_horizontal_offset',
  CardShadowVerticalOffset: 'card_shadow_vertical_offset',
  CardShadowBlur: 'card_shadow_blur',
  ProductImageAspectRatio: 'product_image_aspect_ratio',
  ProductImageCornerRoundness: 'product_image_corner_roundness',
  MaxPageWidth: 'max_page_width',
  MaxContentWidth: 'max_content_width',
  SectionVerticalPadding: 'section_vertical_padding',
  SectionHorizontalPadding: 'section_horizontal_padding',
  HeadingFont: 'heading_font',
  FontFamily: 'font_family',
  EnableStickyNavigationalBlocks: 'enable_sticky_navigational_blocks',
  NavigationBlocksPosition: 'navigation_blocks_position',
  PopupLayout: 'popup_layout',
  CancelButtonPlacement: 'cancel_button_placement',
  ReturnButtonPlacement: 'return_button_placement',
  PauseButtonPlacement: 'pause_button_placement',
  OrderPriceOnListPage: 'order_price_on_list_page',
} as const;

export const OmpHeadingSizeOptions: { value: OmpHeadingSize; label: string }[] = [
  { value: OmpHeadingSize.Small, label: 'Small' },
  { value: OmpHeadingSize.Medium, label: 'Medium' },
  { value: OmpHeadingSize.Large, label: 'Large' },
];

export const OmpPillButtonStyleOptions: { value: OmpPillButtonStyle; label: string }[] = [
  { value: OmpPillButtonStyle.LowFillPill, label: 'Low Fill Pill Button' },
  { value: OmpPillButtonStyle.FillPill, label: 'Fill Pill Button' },
  { value: OmpPillButtonStyle.OutlinePill, label: 'Outline Pill Button' },
  { value: OmpPillButtonStyle.Text, label: 'Text Link' },
];

export const OmpImageRatioOptions: { value: OmpImageRatio; label: string }[] = [
  { value: OmpImageRatio.Adapt, label: 'Adapt to image size' },
  { value: OmpImageRatio.SixteenNine, label: '16:9' },
  { value: OmpImageRatio.FourThree, label: '4:3' },
  { value: OmpImageRatio.Square, label: 'Square (1:1)' },
];

export const OmpTextImageLayoutOptions: { value: OmpTextImageLayout; label: string }[] = [
  { value: OmpTextImageLayout.ImageLeft, label: 'Image left, text right' },
  { value: OmpTextImageLayout.ImageRight, label: 'Image right, text left' },
  { value: OmpTextImageLayout.ImageTop, label: 'Image above text' },
  { value: OmpTextImageLayout.ImageBottom, label: 'Image below text' },
];

export const OmpImageFitOptions: { value: OmpImageFit; label: string }[] = [
  { value: OmpImageFit.Contain, label: 'Fit (whole image visible)' },
  { value: OmpImageFit.Cover, label: 'Fill (crops to the edges)' },
];

export const OmpContentAlignmentOptions: { value: OmpContentAlignment; label: string }[] = [
  { value: OmpContentAlignment.Top, label: 'Top' },
  { value: OmpContentAlignment.Center, label: 'Center' },
  { value: OmpContentAlignment.Bottom, label: 'Bottom' },
];

export const OmpMobileLayoutOptions: { value: OmpMobileLayout; label: string }[] = [
  { value: OmpMobileLayout.ImageFirst, label: 'Image first' },
  { value: OmpMobileLayout.TextFirst, label: 'Text first' },
];

export const OmpCalloutLayoutOptions: { value: OmpCalloutLayout; label: string }[] = [
  { value: OmpCalloutLayout.Inline, label: 'Icon beside the text' },
  { value: OmpCalloutLayout.Stacked, label: 'Icon above the text' },
  { value: OmpCalloutLayout.Banner, label: 'Single row, button on the right' },
];

export const OmpCalloutToneOptions: { value: OmpCalloutTone; label: string }[] = [
  { value: OmpCalloutTone.Brand, label: 'Brand' },
  { value: OmpCalloutTone.Positive, label: 'Positive' },
  { value: OmpCalloutTone.Caution, label: 'Caution' },
  { value: OmpCalloutTone.Plain, label: 'Plain' },
];

export const OmpCalloutIconOptions: { value: OmpCalloutIcon; label: string }[] = [
  { value: OmpCalloutIcon.None, label: 'No icon' },
  { value: OmpCalloutIcon.Sparkles, label: 'Sparkles' },
  { value: OmpCalloutIcon.Gift, label: 'Gift' },
  { value: OmpCalloutIcon.Percent, label: 'Discount' },
  { value: OmpCalloutIcon.Tag, label: 'Price tag' },
  { value: OmpCalloutIcon.Truck, label: 'Shipping' },
  { value: OmpCalloutIcon.Clock, label: 'Time' },
  { value: OmpCalloutIcon.Shield, label: 'Guarantee' },
  { value: OmpCalloutIcon.Info, label: 'Information' },
  { value: OmpCalloutIcon.Alert, label: 'Warning' },
  { value: OmpCalloutIcon.Star, label: 'Star' },
  { value: OmpCalloutIcon.Heart, label: 'Heart' },
  { value: OmpCalloutIcon.Party, label: 'Celebration' },
  { value: OmpCalloutIcon.Custom, label: 'Upload my own…' },
];

export const OmpAccentStyleOptions: { value: OmpAccentStyle; label: string }[] = [
  { value: OmpAccentStyle.None, label: 'None' },
  { value: OmpAccentStyle.LeftBar, label: 'Bar on the left' },
  { value: OmpAccentStyle.TopBar, label: 'Bar on top' },
];

export const OmpVideoAspectOptions: { value: OmpVideoAspect; label: string }[] = [
  { value: OmpVideoAspect.SixteenNine, label: '16:9 (widescreen)' },
  { value: OmpVideoAspect.FourThree, label: '4:3' },
  { value: OmpVideoAspect.Square, label: 'Square (1:1)' },
];

export const OmpColumnsLayoutOptions: { value: OmpColumnsLayout; label: string }[] = [
  { value: OmpColumnsLayout.Equal, label: 'Equal width' },
  { value: OmpColumnsLayout.WideLeft, label: 'Wider left (60/40)' },
  { value: OmpColumnsLayout.WideRight, label: 'Wider right (40/60)' },
];

export const OmpColumnsVerticalAlignOptions: { value: OmpColumnsVerticalAlign; label: string }[] = [
  { value: OmpColumnsVerticalAlign.Stretch, label: 'Equal height' },
  { value: OmpColumnsVerticalAlign.Top, label: 'Top' },
  { value: OmpColumnsVerticalAlign.Center, label: 'Center' },
];

export const OmpColumnChildKeys: string[] = [
  OmpSectionKey.TextAndImage,
  OmpSectionKey.Callout,
  OmpSectionKey.TextBox,
  OmpSectionKey.ImageBanner,
  OmpSectionKey.Video,
];

export const OmpColumnsChildField = 'columns';

export const OmpColumnCountOptions: number[] = [2, 3];

export function isOmpColumnChildKey(key: string): boolean {
  return OmpColumnChildKeys.indexOf(key) !== -1;
}

export const OmpButtonShapeGroup = 'button_shape';

export const OmpButtonGroups: { key: OmpButtonGroup; label: string; help: string }[] = [
  { key: OmpButtonGroup.Primary, label: 'Primary Button', help: 'Main action on a page, such as Send Now.' },
  { key: OmpButtonGroup.Secondary, label: 'Secondary Button', help: 'Supporting action next to the primary one.' },
  { key: OmpButtonGroup.Tertiary, label: 'Tertiary Button', help: 'Low emphasis action, usually plain text.' },
];

export const OmpLogOutBlockKey = 'log_out_label';

export const OmpNavBlocks: { dataKey: string; icon: string; help: string }[] = [
  { dataKey: 'home_label', icon: 'home', help: 'Link that navigates user to the portal home page' },
  { dataKey: 'orders_label', icon: 'orders', help: 'Link that navigates user to a page with their list of orders' },
  {
    dataKey: 'subscriptions_label',
    icon: 'subscriptions',
    help: 'Link that navigates user to a page with their list of subscriptions',
  },
  {
    dataKey: 'address_payment_label',
    icon: 'address-payment',
    help: 'Link that navigates user to their shipping and payment details',
  },
  { dataKey: 'log_out_label', icon: 'log-out', help: 'Link that logs user out and navigates them to the login page' },
];

export const OmpTopSectionOrder: string[] = [
  OmpSectionKey.Header,
  OmpSectionKey.AnnouncementBar,
  OmpSectionKey.WelcomeMessage,
  OmpSectionKey.NavigationalBlocks,
];

export const OmpSectionCatalog: { [key: string]: OmpSectionDef } = {
  [OmpSectionKey.Header]: {
    key: OmpSectionKey.Header,
    name: 'Header',
    description: 'The top bar of the portal, with your logo.',
    position: OmpSectionPosition.Top,
    locked: true,
    deletable: false,
    hasDetailSettings: false,
    addable: false,
    defaultVisible: true,
  },
  [OmpSectionKey.AnnouncementBar]: {
    key: OmpSectionKey.AnnouncementBar,
    name: 'Announcement Bar',
    description: 'Display an important notice across the portal.',
    position: OmpSectionPosition.Top,
    locked: true,
    deletable: true,
    hasDetailSettings: true,
    addable: true,
    defaultVisible: true,
  },
  [OmpSectionKey.WelcomeMessage]: {
    key: OmpSectionKey.WelcomeMessage,
    name: 'Welcome Message',
    description: 'Greet the customer by name at the top of the page.',
    position: OmpSectionPosition.Top,
    locked: true,
    deletable: false,
    hasDetailSettings: true,
    addable: false,
    defaultVisible: true,
  },
  [OmpSectionKey.NavigationalBlocks]: {
    key: OmpSectionKey.NavigationalBlocks,
    name: 'Menu',
    description: 'Links that take customers to the main portal pages.',
    position: OmpSectionPosition.Top,
    locked: true,
    deletable: false,
    hasDetailSettings: true,
    addable: false,
    defaultVisible: true,
  },
  [OmpSectionKey.Footer]: {
    key: OmpSectionKey.Footer,
    name: 'Footer',
    description: 'The bottom bar of the portal.',
    position: OmpSectionPosition.Bottom,
    locked: true,
    deletable: false,
    hideable: true,
    hasDetailSettings: true,
    addable: false,
    defaultVisible: true,
  },

  [OmpSectionKey.NextOrderDetails]: {
    key: OmpSectionKey.NextOrderDetails,
    name: 'Next Order Details',
    description: 'Show the next order and the actions customers can take on it.',
    position: OmpSectionPosition.Body,
    locked: false,
    deletable: false,
    hasDetailSettings: true,
    addable: false,
    defaultVisible: true,
  },
  [OmpSectionKey.MomentCard]: {
    key: OmpSectionKey.MomentCard,
    name: 'Moment Card',
    description: "Show the customer's current moment and the actions they can take on it.",
    position: OmpSectionPosition.Body,
    locked: false,
    deletable: false,
    hasDetailSettings: true,
    addable: false,
    defaultVisible: true,
  },
  [OmpSectionKey.SubscriptionList]: {
    key: OmpSectionKey.SubscriptionList,
    name: 'Subscription List',
    description: 'List the subscriptions a customer has.',
    position: OmpSectionPosition.Top,
    locked: true,
    deletable: false,
    hasDetailSettings: true,
    addable: false,
    defaultVisible: true,
  },
  [OmpSectionKey.OrderList]: {
    key: OmpSectionKey.OrderList,
    name: 'Order List',
    description: 'List the orders a customer has placed.',
    position: OmpSectionPosition.Top,
    locked: true,
    deletable: false,
    hasDetailSettings: true,
    addable: false,
    defaultVisible: true,
  },
  [OmpSectionKey.OrderSummary]: {
    key: OmpSectionKey.OrderSummary,
    name: 'Order Summary',
    description: 'Show the items and total for an order.',
    position: OmpSectionPosition.Body,
    locked: false,
    deletable: false,
    hasDetailSettings: true,
    addable: false,
    defaultVisible: true,
  },

  [OmpSectionKey.CrossSellCarousel]: {
    key: OmpSectionKey.CrossSellCarousel,
    name: 'Cross-Sell Carousel',
    description: 'Showcase additional products customers may want to purchase.',
    position: OmpSectionPosition.Body,
    locked: false,
    deletable: true,
    hasDetailSettings: true,
    addable: true,
    defaultVisible: true,
    // The offers belong to a source product: the page's item on the details pages, the moment item
    // on Home (none on the Default moment, so it shows nothing there).
    pages: [OmpPage.Home, OmpPage.OrderDetails, OmpPage.SubscriptionDetails],
    adminOnly: true,
  },
  [OmpSectionKey.CustomHtml]: {
    key: OmpSectionKey.CustomHtml,
    name: 'Custom HTML',
    description: 'Add custom HTML content or embedded code.',
    position: OmpSectionPosition.Body,
    locked: false,
    deletable: true,
    hasDetailSettings: false,
    addable: true,
    defaultVisible: true,
  },
  [OmpSectionKey.Video]: {
    key: OmpSectionKey.Video,
    name: 'Video',
    description: 'Add a video to inform or engage customers.',
    position: OmpSectionPosition.Body,
    locked: false,
    deletable: true,
    hasDetailSettings: true,
    addable: true,
    repeatable: true,
    defaultVisible: true,
  },
  [OmpSectionKey.Callout]: {
    key: OmpSectionKey.Callout,
    name: 'Callout',
    description: 'Highlight an important message or promotion.',
    position: OmpSectionPosition.Body,
    locked: false,
    deletable: true,
    hasDetailSettings: true,
    addable: true,
    repeatable: true,
    defaultVisible: true,
  },
  [OmpSectionKey.TextAndImage]: {
    key: OmpSectionKey.TextAndImage,
    name: 'Text and Image',
    description: 'Display promotional text and an image with an optional link to a product page or quick checkout.',
    position: OmpSectionPosition.Body,
    locked: false,
    deletable: true,
    hasDetailSettings: true,
    addable: true,
    repeatable: true,
    defaultVisible: true,
  },
  [OmpSectionKey.ImageBanner]: {
    key: OmpSectionKey.ImageBanner,
    name: 'Image Banner',
    description: 'Display a promotional image with an optional link to a product page or quick checkout.',
    position: OmpSectionPosition.Body,
    locked: false,
    deletable: true,
    hasDetailSettings: true,
    addable: true,
    repeatable: true,
    defaultVisible: true,
  },
  [OmpSectionKey.CreditsBalance]: {
    key: OmpSectionKey.CreditsBalance,
    name: 'Credits Balance',
    description: 'Show the store credit available to the customer.',
    position: OmpSectionPosition.Body,
    locked: false,
    deletable: true,
    hasDetailSettings: false,
    addable: true,
    defaultVisible: true,
  },
  [OmpSectionKey.Referrals]: {
    key: OmpSectionKey.Referrals,
    name: 'Referrals',
    description: 'Encourage customers to refer friends and earn rewards.',
    position: OmpSectionPosition.Body,
    locked: false,
    deletable: true,
    hasDetailSettings: false,
    addable: true,
    defaultVisible: true,
  },
  [OmpSectionKey.Columns]: {
    key: OmpSectionKey.Columns,
    name: 'Columns',
    description: 'Organize content into multiple columns.',
    position: OmpSectionPosition.Body,
    locked: false,
    deletable: true,
    hasDetailSettings: true,
    addable: true,
    repeatable: true,
    defaultVisible: true,
  },
  [OmpSectionKey.FaqSection]: {
    key: OmpSectionKey.FaqSection,
    name: 'FAQs',
    description: 'Answer frequently asked customer questions.',
    position: OmpSectionPosition.Body,
    locked: false,
    deletable: true,
    hasDetailSettings: true,
    addable: true,
    defaultVisible: true,
  },
  [OmpSectionKey.Instagram]: {
    key: OmpSectionKey.Instagram,
    name: 'Instagram',
    description: 'Display content from your Instagram account.',
    position: OmpSectionPosition.Body,
    locked: false,
    deletable: true,
    hasDetailSettings: false,
    addable: true,
    defaultVisible: true,
  },
  [OmpSectionKey.TextBox]: {
    key: OmpSectionKey.TextBox,
    name: 'Text Box',
    description: 'Add a simple block of custom text.',
    position: OmpSectionPosition.Body,
    locked: false,
    deletable: true,
    hasDetailSettings: true,
    addable: true,
    repeatable: true,
    defaultVisible: true,
  },
  [OmpSectionKey.ShippingAndPayment]: {
    key: OmpSectionKey.ShippingAndPayment,
    name: 'Shipping and Payment',
    description: 'Display and allow customers to edit their shipping and payment details.',
    position: OmpSectionPosition.Body,
    locked: false,
    deletable: true,
    hasDetailSettings: true,
    addable: true,
    defaultVisible: true,
  },
};

export const OmpPickerSectionKeys: string[] = [
  OmpSectionKey.CrossSellCarousel,
  OmpSectionKey.CustomHtml,
  OmpSectionKey.Video,
  OmpSectionKey.Callout,
  OmpSectionKey.TextAndImage,
  OmpSectionKey.ImageBanner,
  OmpSectionKey.AnnouncementBar,
  OmpSectionKey.CreditsBalance,
  OmpSectionKey.Referrals,
  OmpSectionKey.Columns,
  OmpSectionKey.FaqSection,
  OmpSectionKey.Instagram,
  OmpSectionKey.TextBox,
  OmpSectionKey.ShippingAndPayment,
];

export const OmpDefaultPageSections: { [page: string]: string[] } = {
  [OmpPage.Home]: [
    OmpSectionKey.Header,
    OmpSectionKey.AnnouncementBar,
    OmpSectionKey.WelcomeMessage,
    OmpSectionKey.NavigationalBlocks,
    OmpSectionKey.MomentCard,
    OmpSectionKey.Footer,
  ],
  [OmpPage.Subscriptions]: [
    OmpSectionKey.Header,
    OmpSectionKey.AnnouncementBar,
    OmpSectionKey.NavigationalBlocks,
    OmpSectionKey.SubscriptionList,
    OmpSectionKey.Footer,
  ],
  [OmpPage.SubscriptionDetails]: [
    OmpSectionKey.Header,
    OmpSectionKey.AnnouncementBar,
    OmpSectionKey.NavigationalBlocks,
    OmpSectionKey.NextOrderDetails,
    OmpSectionKey.OrderSummary,
    OmpSectionKey.Footer,
  ],
  [OmpPage.Orders]: [
    OmpSectionKey.Header,
    OmpSectionKey.AnnouncementBar,
    OmpSectionKey.NavigationalBlocks,
    OmpSectionKey.OrderList,
    OmpSectionKey.Footer,
  ],
  [OmpPage.OrderDetails]: [
    OmpSectionKey.Header,
    OmpSectionKey.AnnouncementBar,
    OmpSectionKey.NavigationalBlocks,
    OmpSectionKey.NextOrderDetails,
    OmpSectionKey.OrderSummary,
    OmpSectionKey.Footer,
  ],
};

export const OmpSectionNameByPage: { [pageAndKey: string]: string } = {
  [OmpPage.OrderDetails + '@' + OmpSectionKey.NextOrderDetails]: 'Order Controls',
  [OmpPage.SubscriptionDetails + '@' + OmpSectionKey.NextOrderDetails]: 'Subscription Controls',
  [OmpPage.SubscriptionDetails + '@' + OmpSectionKey.OrderSummary]: 'Subscription Summary',
};

export function ompSectionName(key: string, page: string): string {
  const override = OmpSectionNameByPage[page + '@' + key];
  return override ? override : ompSectionDef(key).name;
}

export function ompDefaultPageSections(page: string, moment: OmpMoment): string[] {
  let list = OmpDefaultPageSections[page] ? OmpDefaultPageSections[page] : [];
  if (page === OmpPage.Home && moment === OmpMoment.None) {
    list = list.filter((key) => key !== OmpSectionKey.MomentCard);
  }
  return list;
}

export function ompMomentSectionKey(page: string): OmpSectionKey {
  let key = OmpSectionKey.NextOrderDetails;
  if (page === OmpPage.Home) {
    key = OmpSectionKey.MomentCard;
  }
  return key;
}

export const OmpMomentActionsField = 'actions';

export const OmpMomentActionDefs: { [key: string]: OmpMomentActionDef } = {
  [OmpMomentActionKey.SendNow]: {
    key: OmpMomentActionKey.SendNow,
    name: 'Bill Now',
    help: 'Button to send the next order now',
    label: 'Send Now',
    icon: 'fa-solid fa-paper-plane',
  },
  [OmpMomentActionKey.Reschedule]: {
    key: OmpMomentActionKey.Reschedule,
    name: 'Adjust Shipping Frequency',
    help: 'Button to change how often the subscription ships',
    label: 'Reschedule',
    icon: 'fa-regular fa-calendar-days',
  },
  [OmpMomentActionKey.Skip]: {
    key: OmpMomentActionKey.Skip,
    name: 'Skip Next Shipment',
    help: 'Button to skip the next shipment',
    label: 'Skip',
    icon: 'fa-solid fa-forward',
  },
  [OmpMomentActionKey.Swap]: {
    key: OmpMomentActionKey.Swap,
    name: 'Swap Products',
    help: 'Button to swap the product — Swap Products on a one-time order, Change Subscription Product on a subscription',
    label: 'Swap',
    icon: 'fa-solid fa-arrow-right-arrow-left',
  },
  [OmpMomentActionKey.BuyAgain]: {
    key: OmpMomentActionKey.BuyAgain,
    name: 'Buy Again',
    help: 'Button to buy again',
    label: 'Buy Again',
    icon: 'fa-solid fa-cart-shopping',
  },
  [OmpMomentActionKey.TrackOrder]: {
    key: OmpMomentActionKey.TrackOrder,
    name: 'Shipment Tracking',
    help: 'Button to track the shipment',
    label: 'Track Order',
    icon: 'fa-solid fa-truck-fast',
  },
  [OmpMomentActionKey.RebillItem]: {
    key: OmpMomentActionKey.RebillItem,
    name: 'Rebill Item',
    help: 'Button to update payment and retry the failed charge',
    label: 'Update Payment',
    icon: 'fa-regular fa-credit-card',
  },
  [OmpMomentActionKey.Reactivate]: {
    key: OmpMomentActionKey.Reactivate,
    name: 'Reactivate Subscription',
    help: 'Button to reactivate the subscription',
    label: 'Reactivate & Save',
    icon: 'fa-solid fa-arrows-rotate',
  },
  [OmpMomentActionKey.ExtendTrial]: {
    key: OmpMomentActionKey.ExtendTrial,
    name: 'Extend Trial',
    help: 'Button to give the customer more time on their trial',
    label: 'Need More Time',
    icon: 'fa-solid fa-hourglass-half',
  },
  [OmpMomentActionKey.AdjustTrialFrequency]: {
    key: OmpMomentActionKey.AdjustTrialFrequency,
    name: 'Adjust Trial Frequency',
    help: 'Button to change how often the subscription ships once the trial ends',
    label: 'Adjust Frequency',
    icon: 'fa-regular fa-calendar-days',
  },
  [OmpMomentActionKey.BillTrialNow]: {
    key: OmpMomentActionKey.BillTrialNow,
    name: 'Bill Trial Now',
    help: 'Button to end the trial and take the first charge now',
    label: 'Bill Now',
    icon: 'fa-solid fa-paper-plane',
  },
  [OmpMomentActionKey.ReturnTrial]: {
    key: OmpMomentActionKey.ReturnTrial,
    name: 'Return Trial',
    help: 'Button to return the trial product',
    label: 'Return Trial',
    icon: 'fa-solid fa-rotate-left',
  },
  [OmpMomentActionKey.CancelTrial]: {
    key: OmpMomentActionKey.CancelTrial,
    name: 'Cancel Trial',
    help: 'Button to cancel the trial',
    label: 'Cancel Trial',
    icon: 'fa-solid fa-xmark',
  },
};

export const OmpMomentActionsByMoment: { [moment: number]: OmpMomentActionKey[] } = {
  [OmpMoment.SubscriptionActive]: [
    OmpMomentActionKey.SendNow,
    OmpMomentActionKey.Reschedule,
    OmpMomentActionKey.Skip,
    OmpMomentActionKey.Swap,
  ],
  [OmpMoment.OrderPending]: [OmpMomentActionKey.BuyAgain, OmpMomentActionKey.Swap, OmpMomentActionKey.Reschedule],
  [OmpMoment.OrderShipped]: [OmpMomentActionKey.TrackOrder, OmpMomentActionKey.BuyAgain],
  [OmpMoment.PaymentIssue]: [OmpMomentActionKey.RebillItem],
  [OmpMoment.SubscriptionCancelled]: [OmpMomentActionKey.Reactivate, OmpMomentActionKey.BuyAgain],
  [OmpMoment.Trial]: [
    OmpMomentActionKey.ExtendTrial,
    OmpMomentActionKey.AdjustTrialFrequency,
    OmpMomentActionKey.BillTrialNow,
    OmpMomentActionKey.ReturnTrial,
    OmpMomentActionKey.CancelTrial,
  ],
};

export const OmpMomentDiscountMoments: OmpMoment[] = [
  OmpMoment.PaymentIssue,
  OmpMoment.SubscriptionCancelled,
  OmpMoment.Trial,
];

export function ompMomentActionKeys(moment: OmpMoment): OmpMomentActionKey[] {
  return OmpMomentActionsByMoment[moment] ? OmpMomentActionsByMoment[moment] : [];
}

export function ompSectionDef(key: string): OmpSectionDef {
  let def = OmpSectionCatalog[key];
  if (!def) {
    const name = key
      .split('_')
      .map((part) => (part ? part.charAt(0).toUpperCase() + part.slice(1) : part))
      .join(' ');
    def = {
      key,
      name,
      description: 'Custom section added to this portal.',
      position: OmpSectionPosition.Body,
      locked: false,
      deletable: true,
      hasDetailSettings: false,
      addable: true,
      defaultVisible: true,
    };
  }
  return def;
}
